import React, { useEffect, useMemo, useState } from 'react';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import {
  deliveryOptionRequiresEmail,
  deliveryOptionRequiresSMS
} from '../../../lib/helpers/communication.helpers';
import {
  CommunicationContextType,
  DeliveryOption,
  IAdHocAudience,
  IAdHocCommunicationPreview
} from '../../../lib/types';
import { buildEmailPreviewDoc, resolvePreviewRecipient } from './preview-panel.helpers';
import { messages } from './preview-panel.messages';
import { IPreviewRecipientOption } from './preview-panel.types';
import { PreviewRecipientSelect } from './preview-recipient-select.component';
import { SmsPreviewBubble } from './sms-preview-bubble.component';
import {
  EmailFrame,
  EmailSubject,
  PreviewContainer,
  PreviewPlaceholder,
  PreviewSurface,
  SurfaceHeader
} from './preview-panel.styles';

interface IPreviewPanelProps {
  assessmentId?: string;
  // Same audience/context the preview + send resolve — scopes the recipient picker.
  criteria: IAdHocAudience;
  contextType?: CommunicationContextType;
  contextId?: string;
  // Debounced delivery option — gates which surfaces render (matches the preview
  // content's own channel gating so a shown surface is never un-previewed).
  deliveryOption: DeliveryOption;
  // Server-rendered preview for the current recipient; undefined while the shared
  // debounced call is skipped (no content) or its first result is in flight.
  preview?: IAdHocCommunicationPreview;
  previewLoading: boolean;
  // The picked "preview as" recipient (null = server-default first recipient).
  selectedRecipient: IPreviewRecipientOption | null;
  onSelectRecipient: (option: IPreviewRecipientOption | null) => void;
  recipientFellOut: boolean;
  onDismissFellOut: () => void;
  skipRecipientQuery: boolean;
}

// Server-substituted compose preview: a "preview as" picker plus email
// (sandboxed iframe) and SMS (bubble) surfaces — all from the shared preview call.
export const PreviewPanel: React.FC<IPreviewPanelProps> = ({
  assessmentId,
  criteria,
  contextType,
  contextId,
  deliveryOption,
  preview,
  previewLoading,
  selectedRecipient,
  onSelectRecipient,
  recipientFellOut,
  onDismissFellOut,
  skipRecipientQuery
}) => {
  // Hold the last settled preview so a debounced refresh doesn't flash the panel
  // empty between keystrokes; clear it only when the call goes idle (no content).
  const [held, setHeld] = useState<IAdHocCommunicationPreview | undefined>(preview);
  useEffect(() => {
    if (preview) setHeld(preview);
    else if (!previewLoading) setHeld(undefined);
  }, [preview, previewLoading]);

  const showEmail = useMemo(() => deliveryOptionRequiresEmail(deliveryOption), [deliveryOption]);
  const showSMS = useMemo(() => deliveryOptionRequiresSMS(deliveryOption), [deliveryOption]);
  const showBothSurfaces = showEmail && showSMS;

  const emailDoc = useMemo(() => buildEmailPreviewDoc(held && held.bodyHtml), [held]);

  // The picker's shown value = the explicit pick, else the server-resolved default.
  const recipientValue = useMemo<IPreviewRecipientOption | null>(
    () => resolvePreviewRecipient(selectedRecipient, held),
    [selectedRecipient, held]
  );

  const hasRecipient = !!(held && held.playerAssessmentId);
  // A settled preview with no recipient means the criteria matched nobody.
  const isEmptyAudience = !!(held && !held.playerAssessmentId);

  return (
    <PreviewContainer aria-live="polite">
      {recipientFellOut && (
        <Alert severity="info" onClose={onDismissFellOut}>
          <IntlMsg {...messages.recipientFellOut} />
        </Alert>
      )}

      {held && (
        <PreviewRecipientSelect
          assessmentId={assessmentId}
          criteria={criteria}
          contextType={contextType}
          contextId={contextId}
          value={recipientValue}
          onChange={onSelectRecipient}
          label={<IntlMsg {...messages.previewAsLabel} />}
          disabled={isEmptyAudience}
          skip={skipRecipientQuery}
        />
      )}

      {!held && (
        <PreviewPlaceholder>
          {previewLoading
            ? <CircularProgress size={20} />
            : (
              <Typography variant="body2" color="textSecondary">
                <IntlMsg {...messages.noContent} />
              </Typography>
            )}
        </PreviewPlaceholder>
      )}

      {isEmptyAudience && (
        <PreviewPlaceholder>
          <Typography variant="body2" color="textSecondary">
            <IntlMsg {...messages.emptyAudience} />
          </Typography>
        </PreviewPlaceholder>
      )}

      {hasRecipient && (
        <Box style={{ opacity: previewLoading ? 0.6 : 1 }} display="flex" flexDirection="column" gridGap={12}>
          {showEmail && (
            <PreviewSurface>
              {showBothSurfaces && (
                <SurfaceHeader>
                  <Typography variant="overline" color="textSecondary">
                    <IntlMsg {...messages.emailHeading} />
                  </Typography>
                </SurfaceHeader>
              )}
              <EmailSubject>
                {held && held.subject ? held.subject : <IntlMsg {...messages.noSubject} />}
              </EmailSubject>
              {/* Empty sandbox — no allow-scripts, no allow-same-origin — is the
                  defense-in-depth boundary around server-rendered HTML. */}
              <EmailFrame sandbox="" srcDoc={emailDoc} title="Email preview" />
            </PreviewSurface>
          )}

          {showSMS && (
            <PreviewSurface>
              {showBothSurfaces && (
                <SurfaceHeader>
                  <Typography variant="overline" color="textSecondary">
                    <IntlMsg {...messages.smsHeading} />
                  </Typography>
                </SurfaceHeader>
              )}
              <SmsPreviewBubble
                segments={(held && held.smsSegments) || []}
                smsBody={held && held.smsBody}
              />
            </PreviewSurface>
          )}
        </Box>
      )}
    </PreviewContainer>
  );
};
